import type { ProjectMessage } from '../transport/api'
import { Icon } from '../ui/Icon'

/**
 * REPLIES, ONE LEVEL, IN A GROUP'S CHAT — owner, 2026-10-20: group 裡面的留言設計成為巢狀的回復, 也就是類似 Facebook
 * 那樣子的 … 其他的使用者可以針對某一則留言在下面回覆 reply, 之後就巢狀的, 只有一層, 就貼在那一則留言的下面 … 其他的
 * 使用者可以選擇展開或者是收起來來看到這個巢狀留言.
 *
 * The chat stack keeps a thread one level deep (messages.ts ReplyFields: a reply to a reply hangs under that one's
 * top). Here the room's messages are sorted into the top-level ones and the replies under each; a reply whose
 * top-level message is older than the history loaded stands on its own with a line saying what it answers, and
 * joins its thread once that page is loaded.
 */

/** The seq a message answers, or null. */
export function replyToOf(m: ProjectMessage): number | null {
  const v = m['reply_to']
  return typeof v === 'number' && v > 0 ? v : null
}

/** The room's messages as threads: the top-level ones in order, and each one's replies in order. */
export function threadMessages(ms: ProjectMessage[]): {
  roots: ProjectMessage[]
  repliesOf: Map<number, ProjectMessage[]>
} {
  const bySeq = new Map<number, ProjectMessage>()
  for (const m of ms) if (m.status !== 'pending' && m.cursor > 0) bySeq.set(m.cursor, m)
  const repliesOf = new Map<number, ProjectMessage[]>()
  const roots: ProjectMessage[] = []
  for (const m of ms) {
    let to = replyToOf(m)
    // One level, whatever arrived: a reply naming a reply belongs to that one's top.
    const parent = to !== null ? bySeq.get(to) : undefined
    const up = parent ? replyToOf(parent) : null
    if (up !== null && bySeq.has(up)) to = up
    if (to !== null && bySeq.has(to) && to !== m.cursor) {
      const list = repliesOf.get(to) ?? []
      list.push(m)
      repliesOf.set(to, list)
    } else {
      roots.push(m)
    }
  }
  return { roots, repliesOf }
}

/** The words of a message, short, for "Replying to …". */
export function snippet(m: ProjectMessage, n = 80): string {
  const body = typeof m.body === 'string' ? m.body : ''
  let text = body
  if (body.startsWith('{')) {
    try {
      const card = JSON.parse(body) as { type?: unknown; text?: unknown }
      if (card.type === 'file') text = typeof card.text === 'string' && card.text.trim() ? card.text : 'a file'
    } catch {
      /* not a card: the text as it is */
    }
  }
  // Cut between characters, never through an emoji's two halves.
  const chars = Array.from(text.replace(/\s+/g, ' ').trim())
  return chars.length > n ? `${chars.slice(0, n - 1).join('')}…` : chars.join('')
}

/** Reply, and the replies' toggle, under a top-level message. */
export function ThreadBar({
  count,
  fresh = 0,
  open,
  onToggle,
  onReply,
}: {
  count: number
  /** Replies the reader has not seen (arrived since the room was drawn): said on the toggle. */
  fresh?: number
  open: boolean
  onToggle: () => void
  /** Absent while the message has no seq yet (still sending). */
  onReply?: () => void
}) {
  if (!onReply && count === 0) return null
  return (
    <div className="pm-thread-bar">
      {onReply && (
        <button type="button" className="pm-thread-btn" onClick={onReply}>
          <Icon name="reply" size={14} />
          Reply
        </button>
      )}
      {count > 0 && (
        <button type="button" className="pm-thread-btn pm-thread-toggle" aria-expanded={open} onClick={onToggle}>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={14} />
          {open ? 'Hide replies' : `View ${count} ${count === 1 ? 'reply' : 'replies'}`}
          {!open && fresh > 0 && <span className="pm-thread-new">{fresh} new</span>}
        </button>
      )}
    </div>
  )
}

/** Above a reply whose top-level message is not loaded: what it answers. */
export function ReplyQuote({ name, text }: { name: string; text: string }) {
  return (
    <div className="pm-reply-quote">
      <Icon name="reply" size={12} />
      <span>
        Replying to <b>{name}</b>
        {text ? <>: “{text}”</> : null}
      </span>
    </div>
  )
}
